/* Renderer de Markdown semântico (ADR-0303, enriquecido em ADR-0722) —
   react-markdown + remark-gfm + rehype-raw + rehype-sanitize + rehype-slug.
   Fundação das docs profundas de packs/app. Habilita, mantendo o sanitize:
   - code blocks com syntax highlight lazy (shiki) + botão copiar (via CodeBlock);
   - ```mermaid``` → diagrama lazy com fallback ao código cru (MermaidDiagram);
   - callouts / GFM alerts `> [!NOTE]` … `> [!CAUTION]` (remarkCallouts);
   - `<details>`/`<summary>` colapsáveis (rehype-raw + allowlist afrouxado);
   - âncoras de heading (rehype-slug, após o sanitize → ids limpos).

   ORDEM dos rehype plugins é crítica: rehype-raw parseia o HTML cru que o
   react-markdown deixa passar (allowDangerousHtml interno), rehype-sanitize limpa
   (allowlist estendido só p/ details/summary/open + classNames de callout), e
   rehype-slug adiciona ids DEPOIS do sanitize (ids derivados do texto = seguros). */
import { type ComponentProps, forwardRef } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/utils.js';
import { CodeBlock } from './code-block.js';
import {
  CALLOUT_CLASSNAMES,
  calloutTypeFromClassName,
  MarkdownCallout,
  remarkCallouts,
} from './markdown-callout.js';
import { MermaidDiagram } from './mermaid-diagram.js';

const LANGUAGE_PREFIX = 'language-';

/* Allowlist do rehype-sanitize afrouxado com cirurgia: details/summary já são
   tagNames padrão; adicionamos `open` em details e o className restrito dos
   callouts em div (só os md-callout-*, nenhum className arbitrário). */
const classNameRule: [string, ...string[]] = ['className', ...CALLOUT_CLASSNAMES];
/* Destructuring (não dot-access) para ler o Record index-signature de attributes:
   evita o conflito TS4111 (noPropertyAccessFromIndexSignature) × useLiteralKeys. */
const baseAttributes = defaultSchema.attributes ?? {};
const { div: divAttributes = [], details: detailsAttributes = [] } = baseAttributes;
const SANITIZE_SCHEMA: typeof defaultSchema = {
  ...defaultSchema,
  attributes: {
    ...baseAttributes,
    div: [...divAttributes, classNameRule],
    details: [...detailsAttributes, 'open'],
  },
};

/** Extrai a linguagem `language-*` das properties hast de um `<code>`. */
function fenceLanguage(properties: Record<string, unknown> | null | undefined): string {
  const { className: raw } = properties ?? {};
  const classes = Array.isArray(raw)
    ? raw.map((entry) => String(entry))
    : typeof raw === 'string'
      ? raw.split(/\s+/)
      : [];
  for (const cls of classes) {
    if (cls.startsWith(LANGUAGE_PREFIX)) return cls.slice(LANGUAGE_PREFIX.length);
  }
  return '';
}

/* Cada componente consome só os props que usa — evita repassar `node` para o DOM. */
const MARKDOWN_COMPONENTS: Components = {
  /* `id` vem do rehype-slug (âncora p/ deep-link/TOC) e é repassado ao heading. */
  h1: ({ children, id }) => (
    <h1 id={id} className="mt-6 mb-3 text-xl font-semibold text-foreground first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children, id }) => (
    <h2 id={id} className="mt-6 mb-2 text-lg font-semibold text-foreground first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children, id }) => (
    <h3 id={id} className="mt-4 mb-2 text-base font-semibold text-foreground first:mt-0">
      {children}
    </h3>
  ),
  h4: ({ children, id }) => (
    <h4 id={id} className="mt-4 mb-1 text-sm font-semibold text-foreground first:mt-0">
      {children}
    </h4>
  ),
  p: ({ children }) => <p className="my-2 text-sm leading-relaxed text-foreground">{children}</p>,
  ul: ({ children }) => (
    <ul className="my-2 ml-5 list-disc space-y-1 text-sm text-foreground">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-2 ml-5 list-decimal space-y-1 text-sm text-foreground">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
    >
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-l-2 border-border pl-3 text-sm text-muted-foreground">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-border" />,
  /* Blocos fenced: extrai lang + texto direto do nó hast (já pós-sanitize) e
     delega ao CodeBlock (highlight lazy + copiar) ou ao MermaidDiagram. O
     `<code>` filho não é re-renderizado (não passamos `children`), então o
     override `code` cobre só inline. Sem nó reconhecível → pre bruto. */
  pre: ({ node, children }) => {
    const codeEl = node?.children?.[0];
    if (codeEl === undefined || codeEl.type !== 'element' || codeEl.tagName !== 'code') {
      return (
        <pre className="my-3 overflow-x-auto rounded-md border border-border bg-muted p-3 text-xs leading-relaxed">
          {children}
        </pre>
      );
    }
    const language = fenceLanguage(codeEl.properties);
    const code = codeEl.children
      .map((child) => (child.type === 'text' ? child.value : ''))
      .join('')
      .replace(/\n$/, '');
    if (language === 'mermaid') {
      return <MermaidDiagram code={code} className="my-3" />;
    }
    return <CodeBlock language={language} code={code} className="my-3" />;
  },
  code: ({ children, className }) => {
    /* Só inline chega aqui (blocos são tratados no `pre`). Fallback defensivo:
       se um bloco escapar, o `language-*` mantém mono sem a pill. */
    const isBlock = new RegExp(`\\b${LANGUAGE_PREFIX}`).test(className ?? '');
    return (
      <code
        className={cn(
          isBlock
            ? 'font-mono text-foreground'
            : 'rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-foreground',
        )}
      >
        {children}
      </code>
    );
  },
  /* Callouts (remarkCallouts marca o div com md-callout-*) e divs neutros. */
  div: ({ className, children }) => {
    const type = calloutTypeFromClassName(className);
    if (type === undefined) {
      return (
        <div className={typeof className === 'string' ? className : undefined}>{children}</div>
      );
    }
    return <MarkdownCallout type={type}>{children}</MarkdownCallout>;
  },
  details: ({ children }) => (
    <details className="my-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm [&[open]>summary]:mb-2">
      {children}
    </details>
  ),
  summary: ({ children }) => (
    <summary className="cursor-pointer font-medium text-foreground marker:text-muted-foreground">
      {children}
    </summary>
  ),
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
  th: ({ children }) => (
    <th className="border-b border-border px-3 py-2 text-left font-medium text-foreground">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border-b border-border px-3 py-2 align-top text-foreground">{children}</td>
  ),
  img: ({ src, alt }) => (
    <img
      src={typeof src === 'string' ? src : undefined}
      alt={alt ?? ''}
      className="my-2 max-w-full rounded-md border border-border"
    />
  ),
};

/* Tipagem derivada do próprio react-markdown (evita importar `unified`, não
   hoisted) — força o tuple `[plugin, options]` em vez de widening p/ array. */
type RemarkPlugins = ComponentProps<typeof ReactMarkdown>['remarkPlugins'];
type RehypePlugins = ComponentProps<typeof ReactMarkdown>['rehypePlugins'];
const REMARK_PLUGINS: RemarkPlugins = [remarkGfm, remarkCallouts];
const REHYPE_PLUGINS: RehypePlugins = [rehypeRaw, [rehypeSanitize, SANITIZE_SCHEMA], rehypeSlug];

export interface MarkdownProps {
  /** Conteúdo Markdown a renderizar. */
  readonly children: string;
  /** Classe do container (espaçamento/scroll a cargo do caller). */
  readonly className?: string | undefined;
}

/** Renderiza Markdown enriquecido (GFM + code highlight + callouts + mermaid +
 *  details + âncoras) com estilos shadcn e sanitização (ADR-0722). */
export const Markdown = forwardRef<HTMLDivElement, MarkdownProps>(
  ({ children, className }, ref) => (
    <div ref={ref} data-slot="markdown" className={cn('text-foreground', className)}>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={MARKDOWN_COMPONENTS}
      >
        {children}
      </ReactMarkdown>
    </div>
  ),
);
Markdown.displayName = 'Markdown';
